import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "../apiClientContext";
import {
    createRoutingRule,
    deleteRoutingRule,
    fetchRoutingRule,
    fetchRoutingRules,
    fetchRoutingTagKeys,
    setRoutingRuleEnabled,
    testRouting,
    updateRoutingRule,
} from "./api";
import type {
    CreateRoutingRuleInput,
    SetRoutingRuleEnabledInput,
    TestRoutingInput,
    UpdateRoutingRuleInput,
} from "./api";
import type { RoutingRule } from "./types";

const routingKeys = {
    all: ["routing"] as const,
    rules: (workflowKind?: string) =>
        workflowKind !== undefined
            ? (["routing", "rules", workflowKind] as const)
            : (["routing", "rules"] as const),
    rule: (id: string) => ["routing", "rule", id] as const,
    tagKeys: ["routing", "tag-keys"] as const,
};

export function useRoutingRules(workflowKind?: string) {
    const client = useApiClient();
    return useQuery({
        queryKey: routingKeys.rules(workflowKind),
        queryFn: () => fetchRoutingRules(client, workflowKind),
        staleTime: 30_000,
        refetchOnWindowFocus: true,
    });
}

export function useRoutingRule(id: string) {
    const client = useApiClient();
    return useQuery({
        queryKey: routingKeys.rule(id),
        queryFn: () => fetchRoutingRule(client, id),
        enabled: id.length > 0,
        staleTime: 30_000,
    });
}

export function useRoutingTagKeys() {
    const client = useApiClient();
    return useQuery({
        queryKey: routingKeys.tagKeys,
        queryFn: () => fetchRoutingTagKeys(client),
        // Tag-key registry only changes on a contracts-package bump that
        // ships with a redeploy; happily cache for the session.
        staleTime: Infinity,
    });
}

function makeRuleMutationHandlers(queryClient: ReturnType<typeof useQueryClient>) {
    return {
        onSuccess: (rule: RoutingRule) => {
            queryClient.setQueryData(routingKeys.rule(rule.id), rule);
            void queryClient.invalidateQueries({ queryKey: routingKeys.all });
        },
    };
}

export function useCreateRoutingRule() {
    const client = useApiClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (input: CreateRoutingRuleInput) => createRoutingRule(client, input),
        ...makeRuleMutationHandlers(queryClient),
    });
}

export function useUpdateRoutingRule() {
    const client = useApiClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (input: UpdateRoutingRuleInput) => updateRoutingRule(client, input),
        ...makeRuleMutationHandlers(queryClient),
    });
}

export function useSetRoutingRuleEnabled() {
    const client = useApiClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (input: SetRoutingRuleEnabledInput) =>
            setRoutingRuleEnabled(client, input),
        ...makeRuleMutationHandlers(queryClient),
    });
}

export function useDeleteRoutingRule() {
    const client = useApiClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id: string) => deleteRoutingRule(client, id),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: routingKeys.all });
        },
    });
}

export function useTestRouting() {
    const client = useApiClient();
    return useMutation({
        mutationFn: (input: TestRoutingInput) => testRouting(client, input),
    });
}
